import React, { useEffect, useState } from "react";

import { formatCurrency, getLocalStorageCropExplorerPseudoUserId, useAddCropExplorationEntry, useDismissRecommendation, useTranslation } from "utils";

import { CROP_TYPE, CropExplorationResponse, RecommendedCropFormData } from "models";

import { AlertBanner, Button, Divider, Flex, Form, Icon, Radio, RadioChangeEvent, RadioPill, RadioPillGroup } from "components";

import {
  RecommendationsWithResiduePayoutPotentials,
  RecommendedCropsWithResiduePayoutPotentials,
} from "pages/app/crop-explorer/components/recommendation-card/RecommendationCardPopover.tsx";

type RecommendationCardFormProp = {
  recommendation: RecommendationsWithResiduePayoutPotentials;
  cropExploration: CropExplorationResponse;
  handleAfterAddOrDismiss: () => void;
  onClose: () => void;
};

export const RecommendationCardForm = ({ recommendation, cropExploration, handleAfterAddOrDismiss, onClose }: RecommendationCardFormProp) => {
  const { t } = useTranslation("cropExplorer");
  const [form] = Form.useForm();
  const [selectionResiduePayout, setSelectionResiduePayout] = useState<number>(0);
  const [selectedCropId, setSelectedCropId] = useState<string | undefined>();
  const { mutate: addCropExplorationEntry, isPending: isPendingAddCropExplorationEntry, isSuccess: isAddSuccess } = useAddCropExplorationEntry();
  const { mutate: dismissRecommendation, isSuccess: isSuccessDismissRecommendation } = useDismissRecommendation();
  const pseudoUserId = getLocalStorageCropExplorerPseudoUserId();
  const showResidue = recommendation.crops.find((crop: RecommendedCropsWithResiduePayoutPotentials) => crop.cropId === selectedCropId)?.residue;

  const formatPayoutValue = (value: number): string => {
    if (!value) return formatCurrency(0, 0);
    return formatCurrency(value, 0, "always");
  };
  const handleSave = (formData: RecommendedCropFormData) => {
    addCropExplorationEntry({
      cropExplorationId: cropExploration.id,
      crop: { cropId: formData.cropId, year: recommendation.year, residue: formData.hasResidue ?? false, type: CROP_TYPE.SIDE, wasRecommended: true },
      pseudoUserId,
    });
  };
  const handleDismiss = () => {
    dismissRecommendation({ cropExplorationId: cropExploration.id, year: recommendation.year, pseudoUserId: pseudoUserId });
  };
  const onCropSelectionChanged = (e: RadioChangeEvent) => {
    form.resetFields();
    form.setFieldsValue({ cropId: e.target.value });
    setSelectedCropId(e.target.value);
  };

  useEffect(() => {
    if (!selectedCropId || !showResidue) return;
    const residuePayoutPotential = recommendation.crops.find((crop) => crop.cropId === selectedCropId)?.residuePayoutPotential || 0;
    setSelectionResiduePayout(residuePayoutPotential);
  }, [cropExploration, selectedCropId, showResidue, recommendation]);

  useEffect(() => {
    if (isAddSuccess || isSuccessDismissRecommendation) handleAfterAddOrDismiss();
  }, [isAddSuccess, isSuccessDismissRecommendation, handleAfterAddOrDismiss]);

  return (
    <Form
      data-testid="crop-recommendation-form"
      className="crop-recommendation-form"
      name={`recommendation-${recommendation.year}`}
      onFinish={handleSave}
      form={form}
    >
      <Flex data-testid={`recommendation-form-${recommendation.year}-header`} className="recommendation-card-form header" justify="space-between">
        <Flex gap=".5rem" align="center" justify="center">
          <Icon dataTestId={`recommendation-form-${recommendation.year}-header-icon`} className="bulb-icon" iconName="tabler/bulb" />
          <h5 data-testid={`recommendation-form-${recommendation.year}-header-title`}>{t("recommendation.form.title")}</h5>
        </Flex>
        <Icon dataTestId={`recommendation-form-${recommendation.year}-close-icon`} className="pointer" iconName="tabler/x" onClick={onClose} />
      </Flex>
      <p data-testid={`recommendation-form-${recommendation.year}-subtitle`} className="paragraph-m-bold margin-top-200 ">
        {t("recommendation.form.subtitle", { year: recommendation.basedOnYears[0] })}
      </p>
      <p data-testid={`recommendation-form-${recommendation.year}-selectLabel`} className="paragraph-m margin-top-125">
        {t("recommendation.form.selectLabel")}
      </p>
      <Form.Item name="cropId" className="margin-top-125" rules={[{ required: true, message: t("cropExplorer:addEditCrop.selectionRequiredErrorMessage") }]}>
        <RadioPillGroup onChange={onCropSelectionChanged} value={selectedCropId}>
          {recommendation.crops.map((recommendedCrop) => (
            <RadioPill
              additionalInfo={`(${t("addEditCrop.avgPayout")} ${formatPayoutValue(recommendedCrop.averagePayoutValue)})`}
              key={recommendedCrop.cropId}
              label={recommendedCrop.cropName}
              value={recommendedCrop.cropId}
              dataTestId={`recommendation-card-form-radio-group-${recommendedCrop.cropId}`}
            />
          ))}
        </RadioPillGroup>
      </Form.Item>
      {showResidue ? (
        <>
          <Divider className="margin-top-125 margin-bottom-100" />
          <p data-testid={`recommendation-card-form-${recommendation.year}-residue-label`} className="residue-label">
            {t("cropExplorer:addEditCrop.residueLeft")}
          </p>
          {selectionResiduePayout !== 0 && (
            <AlertBanner
              className="margin-bottom-100"
              description={selectionResiduePayout > 0 ? t("cropExplorer:addEditCrop.leaveResidueInfo") : t("cropExplorer:addEditCrop.removeResidueInfo")}
              type="info"
              dataTestId="recommendation-card-form-residue-banner"
              iconName="tabler/bulb"
            />
          )}
          <Form.Item
            data-testid="recommendation-card-form-residue"
            className="padding-bottom-100"
            name="hasResidue"
            initialValue={undefined}
            rules={[{ required: true, message: t("cropExplorer:addEditCrop.selectionRequiredErrorMessage") }]}
          >
            <Radio.Group
              data-testid="recommendation-card-form-residue-radio-group"
              className="recommendation-card-form radio-button-group"
              optionType="button"
              buttonStyle="solid"
              options={[
                {
                  value: true,
                  label: (
                    <Flex vertical justify="space-around">
                      <span>{t("cropExplorer:addEditCrop.yes")}</span>
                      {typeof selectionResiduePayout === "number" && selectionResiduePayout !== 0 && (
                        <span className="paragraph-s grey-text" data-testid="recommendation-card-form-residue-payout">
                          {t("cropExplorer:addEditCrop.avgPayout")} {formatCurrency(selectionResiduePayout, 0, "always")}
                        </span>
                      )}
                    </Flex>
                  ),
                },
                {
                  value: false,
                  label: (
                    <Flex vertical justify="space-around">
                      <span>{t("cropExplorer:addEditCrop.no")}</span>
                      {typeof selectionResiduePayout === "number" && selectionResiduePayout !== 0 && (
                        <span className="paragraph-s grey-text" data-testid="recommendation-card-form-residue-comparison">
                          {formatCurrency(0, 0)}
                        </span>
                      )}
                    </Flex>
                  ),
                },
              ]}
            />
          </Form.Item>
        </>
      ) : null}
      <Flex justify="flex-end" align="center" gap="1rem" className="margin-top-200">
        <p className="pointer paragraph-m-bold green-text margin-0" onClick={handleDismiss} data-testid="recommendation-card-form-dismiss-button">
          {t("recommendation.form.dismiss")}
        </p>
        <Button
          dataTestId="crop-explorer-save-button"
          className="crop-explorer save-button"
          type="primary"
          size="small"
          htmlType="submit"
          loading={isPendingAddCropExplorationEntry}
        >
          {t("addEditCrop.save")}
        </Button>
      </Flex>
    </Form>
  );
};
